<script setup>
import { computed, onMounted, reactive, ref } from "vue"
import Swal from "sweetalert2"
import api from "@/api/axios.js"

const registros = ref([])
const cargando = ref(true)

const filtros = reactive({
  fechaIdaDesde: "",
  fechaIdaHasta: "",
  dias: "",
  diasParaViajarMax: "",
  q: ""
})

async function cargarRegistros() {
  cargando.value = true
  try {
    const params = {}
    if (filtros.fechaIdaDesde) params.fechaIdaDesde = filtros.fechaIdaDesde
    if (filtros.fechaIdaHasta) params.fechaIdaHasta = filtros.fechaIdaHasta
    if (filtros.dias) params.dias = filtros.dias
    if (filtros.diasParaViajarMax) params.diasParaViajarMax = filtros.diasParaViajarMax
    if (filtros.q) params.q = filtros.q

    const { data } = await api.get("/contabilidad/registros", { params })
    registros.value = data.registros
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo cargar la contabilidad",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    })
  } finally {
    cargando.value = false
  }
}

function limpiarFiltros() {
  filtros.fechaIdaDesde = ""
  filtros.fechaIdaHasta = ""
  filtros.dias = ""
  filtros.diasParaViajarMax = ""
  filtros.q = ""
  cargarRegistros()
}

// Atajo equivalente al uso real que le daban a la columna "hoy" del
// Excel: ver de un clic quién viaja en los próximos N días.
function filtrarProximosViajes(n) {
  filtros.fechaIdaDesde = ""
  filtros.fechaIdaHasta = ""
  filtros.diasParaViajarMax = n
  cargarRegistros()
}

onMounted(cargarRegistros)

// ─── Formato ──────────────────────────────────────────────────────────────
function formatMoney(value) {
  return new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    minimumFractionDigits: 0
  }).format(value || 0)
}

function formatDate(value) {
  if (!value) return ""
  return new Date(value).toLocaleDateString("es-CO", {
    timeZone: "UTC",
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  })
}

// Prioridad: naranja (pendiente del asesor) > amarillo (margen negativo o
// cliente aún no completa el viaje, porque los costos pueden cambiar en
// el camino) > verde (todo cerrado y en orden).
// Bootstrap/Argon no traen una utilidad .bg-orange lista para usar, por
// eso el color naranja se define a mano en <style scoped>.
const semaforoInfo = {
  verde: { clase: "semaforo-verde", texto: "Todo OK" },
  naranja: { clase: "semaforo-naranja", texto: "Pendiente asesor" },
  amarillo: { clase: "semaforo-amarillo", texto: "Por confirmar" }
}

// ─── Modal crear/editar registro ────────────────────────────────────────────
const modalRegistroVisible = ref(false)
const guardandoRegistro = ref(false)
const editandoId = ref(null)

const formVacio = () => ({
  pasajeros: "",
  destino: "",
  fechaIda: "",
  fechaRegreso: "",
  asistenciaMedica: 0,
  traslados: 0,
  costoHotel: 0,
  costoVuelo: 0,
  localizador: "",
  cobertura: "",
  penalidad: 0,
  venta: 0,
  pendienteAsesor: false,
  observaciones: ""
})

const form = reactive(formVacio())

// dias ya no se digita: se calcula solo a partir de Ida/Regreso (igual
// que en la base de datos, que lo guarda como columna generada).
const diasCalculados = computed(() => {
  if (!form.fechaIda || !form.fechaRegreso) return ""
  const ida = new Date(form.fechaIda)
  const regreso = new Date(form.fechaRegreso)
  const dias = Math.round((regreso - ida) / (1000 * 60 * 60 * 24))
  return Number.isFinite(dias) ? dias : ""
})

function abrirNuevoRegistro() {
  editandoId.value = null
  Object.assign(form, formVacio())
  modalRegistroVisible.value = true
}

function abrirEditarRegistro(registro) {
  editandoId.value = registro.id
  Object.assign(form, {
    pasajeros: registro.pasajeros,
    destino: registro.destino || "",
    fechaIda: registro.fechaIda?.slice(0, 10) || "",
    fechaRegreso: registro.fechaRegreso?.slice(0, 10) || "",
    asistenciaMedica: Number(registro.asistenciaMedica) || 0,
    traslados: Number(registro.traslados) || 0,
    costoHotel: Number(registro.costoHotel) || 0,
    costoVuelo: Number(registro.costoVuelo) || 0,
    localizador: registro.localizador || "",
    cobertura: registro.cobertura || "",
    penalidad: Number(registro.penalidad) || 0,
    venta: Number(registro.venta) || 0,
    pendienteAsesor: !!registro.pendienteAsesor,
    observaciones: registro.observaciones || ""
  })
  modalRegistroVisible.value = true
}

function cerrarModalRegistro() {
  modalRegistroVisible.value = false
}

async function guardarRegistro() {
  if (!form.pasajeros || !form.fechaIda) {
    Swal.fire({ icon: "warning", title: "Faltan datos", text: "Pasajeros y fecha de Ida son obligatorios." })
    return
  }

  guardandoRegistro.value = true
  try {
    if (editandoId.value) {
      await api.put(`/contabilidad/registros/${editandoId.value}`, form)
    } else {
      await api.post("/contabilidad/registros", form)
    }

    modalRegistroVisible.value = false
    await cargarRegistros()

    Swal.fire({
      icon: "success",
      title: editandoId.value ? "Registro actualizado" : "Registro creado",
      timer: 1600,
      showConfirmButton: false
    })
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo guardar",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    })
  } finally {
    guardandoRegistro.value = false
  }
}

async function eliminarRegistro(registro) {
  const { isConfirmed } = await Swal.fire({
    icon: "warning",
    title: "¿Eliminar registro?",
    text: `Se ocultará el registro de "${registro.pasajeros}". El histórico se conserva en la base de datos.`,
    showCancelButton: true,
    confirmButtonText: "Sí, eliminar",
    cancelButtonText: "Cancelar",
    confirmButtonColor: "#f5365c"
  })
  if (!isConfirmed) return

  try {
    await api.delete(`/contabilidad/registros/${registro.id}`)
    await cargarRegistros()
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo eliminar",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    })
  }
}

// ─── Modal detalle (abonos + histórico de cambios) ─────────────────────────
const modalAbonosVisible = ref(false)
const registroAbonos = ref(null)
const abonos = ref([])
const historial = ref([])
const cargandoAbonos = ref(false)
const nuevoAbono = reactive({ monto: null, fecha: "" })
const tabDetalle = ref("abonos")

async function abrirAbonos(registro) {
  registroAbonos.value = registro
  nuevoAbono.monto = null
  nuevoAbono.fecha = ""
  tabDetalle.value = "abonos"
  modalAbonosVisible.value = true
  await cargarAbonos()
}

async function cargarAbonos() {
  if (!registroAbonos.value) return
  cargandoAbonos.value = true
  try {
    const { data } = await api.get(`/contabilidad/registros/${registroAbonos.value.id}`)
    registroAbonos.value = data.registro
    abonos.value = data.abonos
    historial.value = data.historial
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo cargar el detalle del registro",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    })
  } finally {
    cargandoAbonos.value = false
  }
}

function cerrarModalAbonos() {
  modalAbonosVisible.value = false
  registroAbonos.value = null
  abonos.value = []
  historial.value = []
  cargarRegistros()
}

// ─── Formato del histórico de cambios ──────────────────────────────────────
const CAMPOS_HISTORIAL_LABEL = {
  creacion: "Creación",
  pasajeros: "Pasajeros",
  destino: "Destino",
  fechaIda: "Fecha Ida",
  fechaRegreso: "Fecha Regreso",
  asistenciaMedica: "Asistencia Médica",
  traslados: "Traslados",
  costoHotel: "Costo Hotel",
  costoVuelo: "Costo Vuelo",
  localizador: "Localizador",
  cobertura: "Cobertura",
  penalidad: "Penalidad",
  venta: "Venta",
  pendienteAsesor: "Pendiente Asesor",
  observaciones: "Observaciones"
}

const CAMPOS_HISTORIAL_MONEY = new Set([
  "asistenciaMedica", "traslados", "costoHotel", "costoVuelo", "penalidad", "venta"
])
const CAMPOS_HISTORIAL_FECHA = new Set(["fechaIda", "fechaRegreso"])

function formatCampoHistorial(campo) {
  return CAMPOS_HISTORIAL_LABEL[campo] || campo
}

function formatValorHistorial(campo, valor) {
  if (valor === null || valor === undefined || valor === "") return "—"
  if (CAMPOS_HISTORIAL_MONEY.has(campo)) return formatMoney(valor)
  if (CAMPOS_HISTORIAL_FECHA.has(campo)) return formatDate(valor)
  if (campo === "pendienteAsesor") return valor === "true" ? "Sí" : "No"
  return valor
}

function formatFechaHora(value) {
  if (!value) return ""
  return new Date(value).toLocaleString("es-CO", {
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit"
  })
}

async function agregarAbono() {
  if (!nuevoAbono.monto || Number(nuevoAbono.monto) <= 0) {
    Swal.fire({ icon: "warning", title: "Monto inválido", text: "Ingresa un monto mayor a 0." })
    return
  }

  try {
    await api.post(`/contabilidad/registros/${registroAbonos.value.id}/abonos`, {
      monto: nuevoAbono.monto,
      fecha: nuevoAbono.fecha || null
    })
    nuevoAbono.monto = null
    nuevoAbono.fecha = ""
    await cargarAbonos()
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo registrar el abono",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    })
  }
}

async function eliminarAbono(abono) {
  const { isConfirmed } = await Swal.fire({
    icon: "warning",
    title: "¿Eliminar abono?",
    text: `Se eliminará el abono de ${formatMoney(abono.monto)} del ${formatDate(abono.fecha)}.`,
    showCancelButton: true,
    confirmButtonText: "Sí, eliminar",
    cancelButtonText: "Cancelar",
    confirmButtonColor: "#f5365c"
  })
  if (!isConfirmed) return

  try {
    await api.delete(`/contabilidad/abonos/${abono.id}`)
    await cargarAbonos()
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo eliminar el abono",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    })
  }
}

const totalVenta = computed(() =>
  registros.value.reduce((acc, r) => acc + Number(r.venta || 0), 0)
)
const totalDeuda = computed(() =>
  registros.value.reduce((acc, r) => acc + Number(r.deuda || 0), 0)
)
const totalMargen = computed(() =>
  registros.value.reduce((acc, r) => acc + Number(r.margen || 0), 0)
)
</script>

<template>
  <div class="container-fluid py-4">

    <!-- RESUMEN -->
    <div class="row mb-4">
      <div class="col-md-4">
        <div class="card">
          <div class="card-body">
            <p class="text-sm text-muted mb-1">Venta total</p>
            <h5 class="mb-0">{{ formatMoney(totalVenta) }}</h5>
          </div>
        </div>
      </div>
      <div class="col-md-4">
        <div class="card">
          <div class="card-body">
            <p class="text-sm text-muted mb-1">Deuda pendiente</p>
            <h5 class="mb-0 text-danger">{{ formatMoney(totalDeuda) }}</h5>
          </div>
        </div>
      </div>
      <div class="col-md-4">
        <div class="card">
          <div class="card-body">
            <p class="text-sm text-muted mb-1">Margen total</p>
            <h5 class="mb-0 text-success">{{ formatMoney(totalMargen) }}</h5>
          </div>
        </div>
      </div>
    </div>

    <div class="card shadow-sm">
      <div class="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div>
          <h5 class="mb-1">
            <i class="ni ni-money-coins text-primary me-2"></i>
            Contabilidad
          </h5>
          <div class="d-flex flex-wrap gap-3 text-xs text-muted">
            <span><span class="badge semaforo-verde">&nbsp;</span> Todo OK: cerrado, sin pendientes y con margen positivo</span>
            <span><span class="badge semaforo-naranja">&nbsp;</span> Pendiente asesor: falta una acción del asesor que atendió</span>
            <span><span class="badge semaforo-amarillo">&nbsp;</span> Por confirmar: margen negativo o el cliente aún no completa el viaje</span>
          </div>
        </div>
        <button type="button" class="btn btn-primary btn-sm mb-0" @click="abrirNuevoRegistro">
          <i class="ni ni-fat-add me-1"></i> Nuevo registro
        </button>
      </div>

      <div class="card-body">

        <!-- FILTROS -->
        <div class="row g-2 mb-2 align-items-end">
          <div class="col-md-3">
            <label class="form-label text-sm mb-1">Ida desde</label>
            <input type="date" class="form-control form-control-sm" v-model="filtros.fechaIdaDesde">
          </div>
          <div class="col-md-3">
            <label class="form-label text-sm mb-1">Ida hasta</label>
            <input type="date" class="form-control form-control-sm" v-model="filtros.fechaIdaHasta">
          </div>
          <div class="col-md-2">
            <label class="form-label text-sm mb-1">Días (exacto)</label>
            <input type="number" class="form-control form-control-sm" v-model="filtros.dias" placeholder="Ej: 5">
          </div>
          <div class="col-md-4">
            <label class="form-label text-sm mb-1">Buscar (pasajero / destino / localizador)</label>
            <input type="text" class="form-control form-control-sm" v-model="filtros.q" placeholder="Ej: Pérez">
          </div>
        </div>

        <div class="row g-2 mb-3 align-items-end">
          <div class="col-md-4">
            <label class="form-label text-sm mb-1">Viajan en los próximos (días)</label>
            <input type="number" class="form-control form-control-sm" v-model="filtros.diasParaViajarMax" placeholder="Ej: 8">
          </div>
          <div class="col-md-4 d-flex gap-2 align-items-end">
            <button type="button" class="btn btn-outline-warning btn-sm mb-0" @click="filtrarProximosViajes(8)">
              Viajan en ≤ 8 días
            </button>
          </div>
          <div class="col-md-4 d-flex gap-2">
            <button type="button" class="btn btn-outline-primary btn-sm mb-0 w-100" @click="cargarRegistros">
              Filtrar
            </button>
            <button type="button" class="btn btn-outline-secondary btn-sm mb-0 w-100" @click="limpiarFiltros">
              Limpiar
            </button>
          </div>
        </div>

        <div v-if="cargando" class="text-center text-muted py-5">
          <div class="spinner-border spinner-border-sm me-2" role="status"></div>
          Cargando registros...
        </div>

        <div v-else class="table-responsive">
          <table class="table table-striped table-bordered align-middle table-sm">
            <thead class="table-dark">
              <tr>
                <th>Pasajeros</th>
                <th>Destino</th>
                <th>Ida</th>
                <th>Días</th>
                <th>Días para viajar</th>
                <th>Regreso</th>
                <th>Asist. Médica</th>
                <th>Traslados</th>
                <th>Costo Hotel</th>
                <th>Costo Vuelo</th>
                <th>Localizador</th>
                <th>Cobertura</th>
                <th>Penalidad</th>
                <th>Venta</th>
                <th>Abonos</th>
                <th>Deuda</th>
                <th>Margen</th>
                <th>Semáforo</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="registros.length === 0">
                <td colspan="19" class="text-center text-muted py-4">
                  No hay registros para los filtros seleccionados.
                </td>
              </tr>
              <tr v-for="r in registros" :key="r.id">
                <td>{{ r.pasajeros }}</td>
                <td>{{ r.destino }}</td>
                <td>{{ formatDate(r.fechaIda) }}</td>
                <td>{{ r.dias ?? "" }}</td>
                <td :class="Number(r.diasParaViajar) >= 0 && Number(r.diasParaViajar) <= 8 ? 'text-warning fw-bold' : ''">
                  {{ Number(r.diasParaViajar) >= 0 ? r.diasParaViajar : "Ya viajó" }}
                </td>
                <td>{{ formatDate(r.fechaRegreso) }}</td>
                <td>{{ formatMoney(r.asistenciaMedica) }}</td>
                <td>{{ formatMoney(r.traslados) }}</td>
                <td>{{ formatMoney(r.costoHotel) }}</td>
                <td>{{ formatMoney(r.costoVuelo) }}</td>
                <td>{{ r.localizador }}</td>
                <td>{{ r.cobertura }}</td>
                <td>{{ formatMoney(r.penalidad) }}</td>
                <td>{{ formatMoney(r.venta) }}</td>
                <td>{{ formatMoney(r.abonosTotal) }}</td>
                <td :class="Number(r.deuda) > 0 ? 'text-danger fw-bold' : ''">{{ formatMoney(r.deuda) }}</td>
                <td>{{ formatMoney(r.margen) }}</td>
                <td>
                  <span class="badge" :class="semaforoInfo[r.semaforo]?.clase">
                    {{ semaforoInfo[r.semaforo]?.texto || r.semaforo }}
                  </span>
                </td>
                <td>
                  <div class="d-flex gap-1">
                    <button type="button" class="btn btn-sm btn-outline-dark" title="Abonos" @click="abrirAbonos(r)">
                      <i class="bi bi-cash-coin"></i>
                    </button>
                    <button type="button" class="btn btn-sm btn-outline-primary" title="Editar" @click="abrirEditarRegistro(r)">
                      <i class="bi bi-pencil"></i>
                    </button>
                    <button type="button" class="btn btn-sm btn-outline-danger" title="Eliminar" @click="eliminarRegistro(r)">
                      <i class="bi bi-trash"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

      </div>
    </div>

    <!-- MODAL CREAR/EDITAR REGISTRO -->
    <div v-if="modalRegistroVisible" class="modal-backdrop-custom" @click.self="cerrarModalRegistro">
      <div class="modal-dialog-custom card shadow-lg">
        <div class="card-header d-flex justify-content-between align-items-center">
          <h6 class="mb-0">{{ editandoId ? "Editar registro" : "Nuevo registro contable" }}</h6>
          <button type="button" class="btn-close" @click="cerrarModalRegistro"></button>
        </div>
        <div class="card-body">
          <form class="row g-3" @submit.prevent="guardarRegistro">

            <div class="col-md-6">
              <label class="form-label">Pasajeros *</label>
              <input type="text" class="form-control" v-model="form.pasajeros" required>
            </div>

            <div class="col-md-6">
              <label class="form-label">Destino</label>
              <input type="text" class="form-control" v-model="form.destino" placeholder="Ej: Hotel Decameron Cartagena">
              <small class="text-muted text-xs">Campo libre: hacia dónde va el cliente (hotel o destino), no depende del cotizador.</small>
            </div>

            <div class="col-md-6">
              <label class="form-label">Localizador</label>
              <input type="text" class="form-control" v-model="form.localizador">
            </div>

            <div class="col-md-6">
              <label class="form-label">Cobertura</label>
              <input type="text" class="form-control" v-model="form.cobertura" placeholder="Ej: USD 50.000">
            </div>

            <div class="col-md-5">
              <label class="form-label">Fecha Ida *</label>
              <input type="date" class="form-control" v-model="form.fechaIda" required>
            </div>

            <div class="col-md-5">
              <label class="form-label">Fecha Regreso</label>
              <input type="date" class="form-control" v-model="form.fechaRegreso">
            </div>

            <div class="col-md-2">
              <label class="form-label">Días</label>
              <input type="text" class="form-control" :value="diasCalculados" disabled>
              <small class="text-muted text-xs">Se calcula solo (Regreso - Ida)</small>
            </div>

            <div class="col-md-3">
              <label class="form-label">Asistencia Médica</label>
              <input type="number" step="any" class="form-control" v-model.number="form.asistenciaMedica">
            </div>

            <div class="col-md-3">
              <label class="form-label">Traslados</label>
              <input type="number" step="any" class="form-control" v-model.number="form.traslados">
            </div>

            <div class="col-md-3">
              <label class="form-label">Costo Hotel</label>
              <input type="number" step="any" class="form-control" v-model.number="form.costoHotel">
            </div>

            <div class="col-md-3">
              <label class="form-label">Costo Vuelo</label>
              <input type="number" step="any" class="form-control" v-model.number="form.costoVuelo">
            </div>

            <div class="col-md-4">
              <label class="form-label">Penalidad</label>
              <input type="number" step="any" class="form-control" v-model.number="form.penalidad">
            </div>

            <div class="col-md-4">
              <label class="form-label">Venta *</label>
              <input type="number" step="any" class="form-control" v-model.number="form.venta" required>
            </div>

            <div class="col-12">
              <label class="form-label">Observaciones</label>
              <textarea class="form-control" rows="2" v-model="form.observaciones"></textarea>
            </div>

            <div class="col-12">
              <div class="form-check">
                <input type="checkbox" class="form-check-input" id="pendienteAsesor" v-model="form.pendienteAsesor">
                <label class="form-check-label" for="pendienteAsesor">
                  Pendiente del asesor que atendió al cliente
                </label>
              </div>
            </div>

            <div class="col-12 d-flex justify-content-end gap-2 mt-2">
              <button type="button" class="btn btn-outline-secondary mb-0" @click="cerrarModalRegistro">
                Cancelar
              </button>
              <button type="submit" class="btn btn-primary mb-0" :disabled="guardandoRegistro">
                {{ guardandoRegistro ? "Guardando..." : "Guardar" }}
              </button>
            </div>

          </form>
        </div>
      </div>
    </div>

    <!-- MODAL DETALLE (ABONOS + HISTÓRICO) -->
    <div v-if="modalAbonosVisible" class="modal-backdrop-custom" @click.self="cerrarModalAbonos">
      <div class="modal-dialog-custom card shadow-lg">
        <div class="card-header d-flex justify-content-between align-items-center">
          <h6 class="mb-0">Detalle — {{ registroAbonos?.pasajeros }}</h6>
          <button type="button" class="btn-close" @click="cerrarModalAbonos"></button>
        </div>
        <div class="card-body">

          <div class="row text-sm mb-3">
            <div class="col-4"><strong>Venta:</strong> {{ formatMoney(registroAbonos?.venta) }}</div>
            <div class="col-4"><strong>Abonado:</strong> {{ formatMoney(registroAbonos?.abonosTotal) }}</div>
            <div class="col-4">
              <strong>Deuda:</strong>
              <span :class="Number(registroAbonos?.deuda) > 0 ? 'text-danger fw-bold' : 'text-success'">
                {{ formatMoney(registroAbonos?.deuda) }}
              </span>
            </div>
          </div>

          <ul class="nav nav-tabs mb-3">
            <li class="nav-item">
              <button type="button" class="nav-link" :class="{ active: tabDetalle === 'abonos' }" @click="tabDetalle = 'abonos'">
                Abonos
              </button>
            </li>
            <li class="nav-item">
              <button type="button" class="nav-link" :class="{ active: tabDetalle === 'historial' }" @click="tabDetalle = 'historial'">
                Histórico de cambios
              </button>
            </li>
          </ul>

          <div v-if="cargandoAbonos" class="text-center text-muted py-3">
            <div class="spinner-border spinner-border-sm"></div>
          </div>

          <template v-else-if="tabDetalle === 'abonos'">
            <form class="row g-2 align-items-end mb-3" @submit.prevent="agregarAbono">
              <div class="col-5">
                <label class="form-label text-sm mb-1">Monto</label>
                <input type="number" step="any" class="form-control form-control-sm" v-model.number="nuevoAbono.monto">
              </div>
              <div class="col-4">
                <label class="form-label text-sm mb-1">Fecha</label>
                <input type="date" class="form-control form-control-sm" v-model="nuevoAbono.fecha">
              </div>
              <div class="col-3">
                <button type="submit" class="btn btn-primary btn-sm mb-0 w-100">Agregar</button>
              </div>
            </form>

            <div class="table-responsive">
              <table class="table table-sm table-bordered">
                <thead class="table-light">
                  <tr>
                    <th>Fecha</th>
                    <th>Monto</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="abonos.length === 0">
                    <td colspan="3" class="text-center text-muted">Sin abonos registrados.</td>
                  </tr>
                  <tr v-for="a in abonos" :key="a.id">
                    <td>{{ formatDate(a.fecha) }}</td>
                    <td>{{ formatMoney(a.monto) }}</td>
                    <td class="text-end">
                      <button type="button" class="btn btn-sm btn-outline-danger" @click="eliminarAbono(a)">
                        <i class="bi bi-trash"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>

          <template v-else>
            <p class="text-xs text-muted">
              Registro append-only: cada modificación queda guardada aquí, no se puede editar ni borrar.
            </p>
            <div class="table-responsive">
              <table class="table table-sm table-bordered">
                <thead class="table-light">
                  <tr>
                    <th>Fecha</th>
                    <th>Usuario</th>
                    <th>Campo</th>
                    <th>Valor anterior</th>
                    <th>Valor nuevo</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="historial.length === 0">
                    <td colspan="5" class="text-center text-muted">Sin cambios registrados.</td>
                  </tr>
                  <tr v-for="h in historial" :key="h.id">
                    <td>{{ formatFechaHora(h.createdAt) }}</td>
                    <td>{{ h.usuarioNombre || "—" }}</td>
                    <td>{{ formatCampoHistorial(h.campo) }}</td>
                    <td>{{ formatValorHistorial(h.campo, h.valorAnterior) }}</td>
                    <td>{{ formatValorHistorial(h.campo, h.valorNuevo) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>

        </div>
      </div>
    </div>

  </div>
</template>

<style scoped>
.modal-backdrop-custom {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  padding: 1rem;
}

.modal-dialog-custom {
  width: 100%;
  max-width: 900px;
  max-height: 90vh;
  overflow-y: auto;
}

.semaforo-verde,
.semaforo-naranja,
.semaforo-amarillo {
  color: #fff;
}

.semaforo-verde {
  background-color: #2dce89;
}

.semaforo-naranja {
  background-color: #fd7e14;
}

.semaforo-amarillo {
  background-color: #fbb140;
  color: #212529;
}
</style>
